import { useCallback, useEffect, useMemo, useState } from "react";
import type { RdScheduleSlot } from "../../../../../shared/rd-schedule-actions";
import {
  fetchOpenTimes,
  zonedInstant,
  type ScheduleApi,
} from "../../../lib/rd-schedule-actions";

// NO-876. The date, open-times and outside-hours choice that New appointment
// and Reschedule share. It owns the open-times read, the chosen slot, and the
// reload a slot conflict asks for ("fresh times").

export interface TimePick {
  status: "idle" | "loading" | "ready" | "error";
  slots: RdScheduleSlot[];
  hoursSet: boolean;
  timeZone: string | null;
  durationMinutes: number | null;
  selected: string | null;
  outside: boolean;
  outsideTime: string;
  /** The chosen instant, or null until a time is picked. */
  startsAt: string | null;
  /** `HH:MM` of the chosen time in the RD's zone. */
  localTime: string | null;
  select: (startsAt: string) => void;
  setOutside: (outside: boolean) => void;
  setOutsideTime: (time: string) => void;
  /** Reads the day again and drops a chosen slot that is no longer open. */
  reload: () => void;
}

interface Loaded {
  key: string;
  slots: RdScheduleSlot[];
  hoursSet: boolean;
  timeZone: string;
  durationMinutes: number;
}

export interface TimePickInput {
  api: ScheduleApi;
  rdId: string | null;
  date: string;
  appointmentTypeId: string | null;
  excludeAppointmentId?: string;
  /** The RD's zone before the first read lands. */
  fallbackTimeZone: string;
}

export function useTimePick(input: TimePickInput): TimePick {
  const { api, rdId, date, appointmentTypeId, excludeAppointmentId, fallbackTimeZone } =
    input;
  const [nonce, setNonce] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [outside, setOutside] = useState(false);
  const [outsideTime, setOutsideTime] = useState("");
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [failedKey, setFailedKey] = useState<string | null>(null);
  const key = `${rdId ?? ""}|${date}|${appointmentTypeId ?? ""}|${nonce}`;

  useEffect(() => {
    if (!appointmentTypeId || !date) return undefined;

    let current = true;

    fetchOpenTimes(api, { rdId, date, appointmentTypeId, excludeAppointmentId })
      .then((response) => {
        if (!current) return;

        const { slots, hoursSet, timeZone, durationMinutes } = response.data;
        setLoaded({ key, slots, hoursSet, timeZone, durationMinutes });
        setFailedKey(null);
        setSelected((chosen) =>
          chosen && slots.some((slot) => slot.startsAt === chosen) ? chosen : null
        );
      })
      .catch(() => {
        if (current) setFailedKey(key);
      });

    return () => {
      current = false;
    };
  }, [api, rdId, date, appointmentTypeId, excludeAppointmentId, key]);

  const status = pickStatus({ appointmentTypeId, date, key, loaded, failedKey });
  const ready = loaded?.key === key ? loaded : null;
  const timeZone = loaded?.timeZone ?? fallbackTimeZone;
  const slots = useMemo(() => ready?.slots ?? [], [ready]);
  const settled = outside || status === "ready";
  const startsAt = settled
    ? resolveStartsAt({ outside, outsideTime, selected, date, timeZone })
    : null;
  const localTime = settled
    ? resolveLocalTime({ outside, outsideTime, selected, slots })
    : null;
  const reload = useCallback(() => setNonce((value) => value + 1), []);
  const select = useCallback((value: string) => setSelected(value), []);

  return {
    status,
    slots,
    hoursSet: ready?.hoursSet ?? true,
    timeZone: loaded?.timeZone ?? null,
    selected,
    outside,
    outsideTime,
    startsAt,
    localTime,
    select,
    setOutside,
    setOutsideTime,
    reload,
    durationMinutes: loaded?.durationMinutes ?? null,
  };
}

function pickStatus(input: {
  appointmentTypeId: string | null;
  date: string;
  key: string;
  loaded: Loaded | null;
  failedKey: string | null;
}): TimePick["status"] {
  if (!input.appointmentTypeId || !input.date) return "idle";

  if (input.failedKey === input.key) return "error";

  return input.loaded?.key === input.key ? "ready" : "loading";
}

function resolveStartsAt(input: {
  outside: boolean;
  outsideTime: string;
  selected: string | null;
  date: string;
  timeZone: string;
}): string | null {
  if (!input.outside) return input.selected;

  if (!input.outsideTime || !input.date) return null;

  return zonedInstant(input.date, input.outsideTime, input.timeZone);
}

function resolveLocalTime(input: {
  outside: boolean;
  outsideTime: string;
  selected: string | null;
  slots: RdScheduleSlot[];
}): string | null {
  if (input.outside) return input.outsideTime || null;

  return (
    input.slots.find((slot) => slot.startsAt === input.selected)?.localTime ?? null
  );
}
